import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { freeComics } from '@/mocks/comics';

interface FreeComicPopupProps {
  onClose: () => void;
}

export default function FreeComicPopup({ onClose }: FreeComicPopupProps) {
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-foreground-950/70 backdrop-blur-sm animate-fadeIn"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="免费漫画弹窗入口"
    >
      <div className="relative w-full max-w-[720px] max-h-[85vh] bg-background-50 rounded-2xl overflow-hidden shadow-2xl animate-scaleIn">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-foreground-950/20 text-background-50 hover:bg-foreground-950/40 transition-colors cursor-pointer"
          aria-label="关闭弹窗"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        <div className="relative bg-gradient-to-br from-accent-500 via-primary-500 to-accent-700 px-8 py-10 text-center overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-background-50" />
            <div className="absolute -bottom-10 -right-10 w-60 h-60 rounded-full bg-background-50" />
          </div>
          <div className="relative z-10">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-50/20">
              <i className="ri-fire-line text-3xl text-background-50"></i>
            </div>
            <h2 className="text-2xl md:text-3xl font-heading text-background-50 mb-2">
              免费漫画限时阅读
            </h2>
            <p className="text-background-200 text-sm">
              海量正版韩漫，无需付费即可畅读热门章节
            </p>
          </div>
        </div>

        <div className="px-6 py-6 max-h-[50vh] overflow-y-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {freeComics.map(comic => (
              <Link
                key={comic.id}
                to={`/comic/${comic.id}`}
                onClick={onClose}
                className="group block rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-accent-300 transition-all duration-300"
              >
                <div className="relative aspect-[3/4] overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`免费阅读 - ${comic.title}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 via-transparent to-transparent" />
                  <div className="absolute bottom-2 left-2 right-2">
                    <span className="px-2 py-0.5 rounded-full bg-accent-500 text-background-50 text-xs font-medium whitespace-nowrap">
                      免费
                    </span>
                  </div>
                </div>
                <div className="p-2.5">
                  <h3 className="text-sm font-semibold text-foreground-900 truncate group-hover:text-accent-600 transition-colors">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1 truncate">
                    {comic.author} · 共 {comic.chapters.length} 话
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="px-6 pb-6 text-center">
          <p className="text-xs text-foreground-400 mb-3">
            以上漫画均可免费阅读部分章节，点击立即开始
          </p>
          <Link
            to="/category/all"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-compass-3-line"></i>
            浏览全部漫画
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.25s ease-out; }
        .animate-scaleIn { animation: scaleIn 0.3s ease-out; }
      `}</style>
    </div>
  );
}